/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import type {
  GroupsFilterByInput,
  GroupsFilterInput,
} from "~/generated/graphql";
import {
  FilterComparisonOperator,
  GroupsFilterByFields,
} from "~/generated/graphql";
import { isEmpty, isNotEmpty, isNotNil } from "~/tools";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { ResourceGroupQuery } from "~/views/resourceGroups/types";

const isQueryStateDate = (queryState: ResourceGroupQuery): boolean => {
  return (
    isNotNil(queryState.startDateUTC) &&
    isNotEmpty(queryState.startDateUTC) &&
    isNotNil(queryState.endDateUTC) &&
    isNotEmpty(queryState.endDateUTC)
  );
};

export const prepareResourceGroupsFilterBy = (
  queryState: ResourceGroupQuery
): GroupsFilterByInput[] | undefined => {
  const createdAtFilter = isQueryStateDate(queryState)
    ? [
        {
          field: GroupsFilterByFields.CreatedAt,
          operator: FilterComparisonOperator.Ge,
          values: [queryState.startDateUTC ?? ""],
        },
        {
          field: GroupsFilterByFields.CreatedAt,
          operator: FilterComparisonOperator.Le,
          values: [queryState.endDateUTC ?? ""],
        },
      ]
    : [];

  const typesFilter = createFilterByInput<GroupsFilterByFields>(
    GroupsFilterByFields.Type,
    queryState.types ?? [],
    queryState.excludeTypes ?? false
  );

  const retVal = [...createdAtFilter, ...typesFilter];

  return isEmpty(retVal) ? undefined : retVal;
};

export const prepareResourceGroupsFilter = (
  queryState: ResourceGroupQuery
): GroupsFilterInput | undefined => {
  const phrase = queryState.searchPhrase?.trim();
  const filterBy = prepareResourceGroupsFilterBy(queryState);
  const hasPhrase = phrase && isNotEmpty(phrase);

  if (!hasPhrase && !filterBy) {
    return undefined;
  }

  return {
    filterBy,
    phrase: hasPhrase ? phrase : undefined,
  };
};
